import React from "react";
import { ClickAwayListener, Divider, Tooltip } from "@mui/material";
import { Dropdown } from "react-bootstrap";
import { MoreVert as ThreeDotIcon } from "@mui/icons-material";
import ReactDOM from "react-dom";
import "./styles.css";  // We'll create this file next

// The kebab toggle's square hit box (px). Exported so any circular badge that
// WRAPS this toggle (e.g. the Collections card's CollectionCoverActions) can
// size itself to CONTAIN the toggle rather than let it poke past the circle
// (Kelly 2026-08-24 #4 / G-139). Meets the 32px desktop click-target floor.
export const MENU_POPUP_TOGGLE_SIZE = 32;

const MenuPopup = ({
  setOpen,
  open,
  handleClose,
  menuItems,
  data,
  maxHeight = "270",
  bottom = 3,
  isShowPortal = false,
  direction = undefined,
  hideToggleIcon = true, // New prop to control toggle icon visibility
  // QA-1016 / G-139 + G-185 — opt-in for the Collections card kebab (and any
  // future circular-badge kebab). When true:
  //   (1) the container's `left`/`bottom` nudge is DROPPED so the toggle stays
  //       flex-centered inside its round badge (the nudge shoved the 20px glyph
  //       +10px right / ~5px up out of the white circle — a measured G-139
  //       break), and
  //   (2) the click handler that opens the menu lives on the whole 32px TOGGLE
  //       button (with stopPropagation) instead of only the 20px glyph, so the
  //       ENTIRE badge is the hit target and a click on the white circle can no
  //       longer fall through to the card's own onClick (the G-185 hit-target
  //       bug: clicking the circle opened the collection, not the menu).
  // Off by default → every other MenuPopup usage is byte-for-byte unchanged.
  centerToggle = false,
}) => {
  // With centerToggle the toggle button itself owns the open handler so the
  // full hit box works and the event never bubbles to a parent card-open click.
  const handleToggleClick = (event) => {
    event.stopPropagation();
    setOpen(data?.id);
  };
  const renderMenuContent = () => (
    <Dropdown.Menu
      variant=" shadow"
      style={{
        minWidth: 180,
        zIndex: 1000000,
      }}
    >
      {menuItems.map((item, index) => (
        <React.Fragment key={index}>
          <Dropdown.Item
            onClick={(event) => {
              event.stopPropagation();
              !item?.isDisabled && handleClose();
              !item?.isDisabled && item?.action(data);
            }}
            disabled={!!item?.isDisabled}
            style={{
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            {item.icon}
            <span style={{ marginLeft: 8, marginRight: "5px" }}>
              {item.label}
            </span>
          </Dropdown.Item>
          {index < menuItems.length - 1 && (
            <Divider style={{ backgroundColor: "#e0e0e0" }} />
          )}
        </React.Fragment>
      ))}
    </Dropdown.Menu>
  );
  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Dropdown
        show={open}
        data-testid="menu-popup-container"
        // centerToggle drops the left/bottom nudge so the toggle sits dead-
        // centre in its badge (QA-1016). The portalled menu is Popper-anchored
        // to the toggle, so it follows the toggle and needs no container nudge.
        style={centerToggle ? undefined : { bottom: bottom, left: 10 }}
        autoClose="outside"
        drop={direction}
      >
        <Tooltip title="More options">
          <Dropdown.Toggle
            variant="none"
            className="no-toggle-icon"
            aria-label="More options"
            onClick={centerToggle ? handleToggleClick : undefined}
            // 32×32 centered hit box: meets the 32px desktop click-target floor
            // (Gordon ruling) — the bare 20px MoreVert glyph measured 20×24px.
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              width: MENU_POPUP_TOGGLE_SIZE,
              height: MENU_POPUP_TOGGLE_SIZE,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            id="dropdown-autoclose-true"
          >
            <ThreeDotIcon
              style={{ fontSize: 20, cursor: "pointer" }}
              // When centerToggle owns the click on the whole button, the glyph
              // must NOT also handle it (would double-fire / re-open). The glyph
              // click stays only for the legacy (non-centered) usages.
              onClick={
                centerToggle
                  ? undefined
                  : (event) => {
                      event.stopPropagation();
                      setOpen(data?.id);
                    }
              }
            />
          </Dropdown.Toggle>
        </Tooltip>
        {isShowPortal
          ? ReactDOM.createPortal(renderMenuContent(), document.body)
          : renderMenuContent()}
      </Dropdown>
    </ClickAwayListener>
  );
};

export default MenuPopup;
